// File: src/pages/AuthForm.jsx
// -----------------------------------------------------------------------------
// Formulaire d'inscription — largeur fixe des champs, pas d'agrandissement
// -----------------------------------------------------------------------------
import React, { useState, useContext, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { validateInviteCode } from "../api/invites";

import { Mail, User, Calendar, Loader2, LockKeyhole, Ticket } from "lucide-react";

export default function AuthForm() {
  const navigate = useNavigate();
  const { signup, login, password, authError, setAuthError } = useContext(AuthContext);
  const [loadingAuth, setLoadingAuth] = useState(false)
  const [confirmResetPwdMessage, setConfirmResetPwdMessage] = useState('')

  const [params] = useSearchParams();
  const section = params.get('section');
  const [values, setValues] = useState({
    firstName: "",
    email: "",
    password: "",
    age: "",
    gender: "boy",
    school_level: "",
    is_premium: false,
    inviteCode: "",
  });
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState(section ? section : (params.get('invite') ? 'signup' : 'login'));
  const [isCodeValid, setIsCodeValid] = useState(false);
  const [isValidatingCode, setIsValidatingCode] = useState(false);

  // Pré-remplir le code d'invitation depuis l'URL
  useEffect(() => {
    const code = params.get('invite');
    if (code && form === 'signup') {
      setValues((v) => ({ ...v, inviteCode: code }));
      validateCodeFormat(code);
    }
  }, [params, form]);

  // Validation du format ORIA-XXXXXXXX
  const validateCodeFormat = async (code) => {
    const codePattern = /^ORIA-\d{8}$/;
    if (codePattern.test(code)) {
      setIsValidatingCode(true);
      try {
        const inviteData = await validateInviteCode(code);
        setIsCodeValid(true);
        setErrors((e) => ({ ...e, inviteCode: undefined }));

        // Pré-remplir les champs avec les données de l'invite
        if (inviteData) {
          setValues((v) => ({
            ...v,
            firstName: inviteData.child_firstname || v.firstName,
            email: inviteData.child_email || v.email,
            age: inviteData.child_age || v.age,
            gender: inviteData.child_gender || v.gender,
            is_premium: inviteData.parent.is_premium,
            school_level: inviteData.school_level || v.school_level
          }));
        }
      } catch (error) {
        setIsCodeValid(false);
        setErrors((e) => ({ ...e, inviteCode: "Code d'invitation invalide" }));
      } finally {
        setIsValidatingCode(false);
      }
    } else {
      setIsCodeValid(false);
      if (code.length > 0) {
        setErrors((e) => ({ ...e, inviteCode: "Format: ORIA-XXXXXXXX (8 chiffres)" }));
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));

    // Validation automatique du code d'invitation
    if (name === 'inviteCode') {
      validateCodeFormat(value);
    }
  };

  const togglePwd = () => {
    setErrors({})
    setConfirmResetPwdMessage("")
    setAuthError(null)
    form === 'pwd' ? setForm('login') : setForm('pwd')
  }

  const toggleForm = () => {
    setErrors({})
    setAuthError(null)
    form === 'signup' ? setForm('login') : setForm('signup')
  }

  const validate = () => {
    const e = {};
    if (form === 'signup' && values.firstName.trim().length < 1) e.firstName = "Prénom trop court";
    if (!/^\S+@\S+\.\S+$/.test(values.email)) e.email = "Email invalide";
    if ( form !== 'pwd' && values.password.trim().length < 2 ) e.password = "Mot de passe obligatoire";
    const ageNum = Number(values.age);
    if (form === 'signup' && (!ageNum || ageNum < 5 || ageNum > 120)) e.age = "Âge invalide";
    if (form === 'signup' && !values.gender) e.gender = "Choisissez un sexe";
    if (form === 'signup' && !isCodeValid) e.inviteCode = "Code d'invitation valide requis";
    if (Object.keys(e).length !== 0) setLoadingAuth(false)
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  async function handleSubmit(e) {
    setLoadingAuth(true)
    e.preventDefault();
    if (!validate()) return;
    const newUser = (form === 'signup') ? await signup(values) : (form === 'login' ? await login(values) : await password(values) );
    setLoadingAuth(false)

    if ( form === 'pwd' ) setConfirmResetPwdMessage("Un email vous a été envoyé.")
    if ( newUser &&  form !== 'pwd' ) navigate("/mon-espace");
  };

  const baseInput =
    "w-full border border-custom-700 rounded-xl bg-gray-800 border-opacity-20 border-teal-400 px-10 py-2 placeholder-custom-500 focus:outline-none focus:ring-2 focus:ring-custom-700 transition";
  const errorInput = "border-red-500 focus:ring-red-400";

  return (
    <div className="flex items-center justify-center p-4 w-full">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md bg-gray-800/30 p-8 rounded-2xl shadow-xl"
      >
        <div className=" space-y-6">
          <h1 className="text-2xl font-semibold text-center mb-4 text-white">
          {form === 'signup' ? 'Inscription' : (form === 'pwd' ? 'Réinitialisation de mot de passe' : 'Connexion')}
          </h1>


          {/* Prénom */}
          {form === 'signup' && (
            <div className="relative hidden">
              <User className="absolute left-3 top-1/2 -translate-y-1/2" size={18} />
              <input
                type="text"
                name="firstName"
                value={values.firstName}
                onChange={handleChange}
                placeholder="Prénom"
                className={`${baseInput} ${errors.firstName ? errorInput : ""}`}
              />
              {errors.firstName && (
                <p className="absolute -bottom-5 left-0 text-xs text-red-500">
                  {errors.firstName}
                </p>
              )}
            </div>
          )}

          {/* Code d'invitation */}
          {form === 'signup' && (
            <div className="relative">
              <Ticket className="absolute left-3 top-1/2 -translate-y-1/2" size={18} />
              <input
                type="text"
                name="inviteCode"
                value={values.inviteCode}
                onChange={handleChange}
                placeholder="Code d'invitation (ORIA-XXXXXXXX)"
                className={`${baseInput} ${errors.inviteCode ? errorInput : ""} ${isCodeValid ? 'border-green-500' : ''}`}
              />
              {isValidatingCode && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 animate-spin" />
              )}
              {isCodeValid && !isValidatingCode && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500 text-xl">✓</span>
              )}
              {errors.inviteCode && (
                <p className="absolute -bottom-5 left-0 text-xs text-red-500">
                  {errors.inviteCode}
                </p>
              )}
            </div>
          )}

          {/* Email */}
          {confirmResetPwdMessage === '' && (
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2" size={18} />
              <input
                type="email"
                name="email"
                value={values.email}
                onChange={handleChange}
                placeholder="Email"
                className={`${baseInput} ${errors.email ? errorInput : ""}`}
              />
              {errors.email && (
                <p className="absolute -bottom-5 left-0 text-xs text-red-500">
                  {errors.email}
                </p>
              )}
            </div>
          )}
          {/* Password */}
          {form !== 'pwd' && (
            <div className="relative">
              <LockKeyhole className="absolute left-3 top-1/2 -translate-y-1/2" size={18} />
              <input
                type="password"
                name="password"
                value={values.password}
                onChange={handleChange}
                placeholder="Mot de passe"
                className={`${baseInput} ${errors.password ? errorInput : ""}`}
              />
              {errors.password && (
                <p className="absolute -bottom-5 left-0 text-xs text-red-500">
                  {errors.password}
                </p>
              )}
            </div>
          )}

          {/* Age */}
          {form === 'signup' && (
            <div className="relative hidden">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2" size={18} />
              <input
                type="number"
                name="age"
                value={values.age}
                onChange={handleChange}
                placeholder="Âge"
                className={`${baseInput} ${errors.age ? errorInput : ""}`}
              />
              {errors.age && (
                <p className="absolute -bottom-5 left-0 text-xs text-red-500">
                  {errors.age}
                </p>
              )}
            </div>
          )}

          {/* Sexe */}
          {form === 'signup' && (
            <fieldset className="hidden flex items-center gap-6">
              <legend className="sr-only">Tu es ?</legend>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="gender"
                  value="boy"
                  checked={values.gender === "boy"}
                  onChange={handleChange}
                  className="accent-primary"
                />
                Un garçon
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="gender"
                  value="girl"
                  checked={values.gender === "girl"}
                  onChange={handleChange}
                  className="accent-primary"
                />
                Une fille
              </label>
            </fieldset>)}
            {form === 'signup' && errors.gender && (
              <p className="text-xs text-red-500 -mt-4 hidden">{errors.gender}</p>
            )}

          {confirmResetPwdMessage === '' && (
            <button
              type="submit"
              disabled={loadingAuth}
              className="inline-flex w-full items-center justify-center btn"
            >
              {loadingAuth ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                form === 'signup' ? 'Je m\'inscris' : (form === 'pwd' ? 'Réinitialiser': 'Je me connecte')
              )}
            </button>
          )}
          {form === 'signup' && <div className="text-center text-sm">En continuant, tu acceptes nos <a href="https://www.oria-avenir.com/cgu-light" className="!underline">conditions d'utilisation</a></div>}
          {authError && (
            <span
              className="cursor-pointer transition-colors text-red-500 text-center w-full block mt-1"
            >
              {authError}
            </span>
          )}
          {confirmResetPwdMessage !== '' && authError === null && (
            <span
              className="cursor-pointer transition-colors text-center w-full block mt-1"
            >
              {confirmResetPwdMessage}
            </span>
          )}
        </div>
        {form !== 'pwd' &&  (
          <span
            onClick={toggleForm}
            className="underline cursor-pointer transition-colors text-[11px] text-center w-full block mt-4"
          >
            {form === 'signup' ? 'Vous avez déjà un compte, connectez-vous ?' : 'Vous n\'avez pas de compte, inscrivez-vous ?'}
          </span>
        )}
        {form === 'login' && (
          <span
            onClick={togglePwd}
            className="underline cursor-pointer transition-colors text-[11px] text-center w-full block mt-2"
          >
            Mot de passe oublié ?
          </span>
          )}
        {form === 'pwd' && (
          <span
            onClick={togglePwd}
            className="underline cursor-pointer transition-colors text-[11px] text-center w-full block mt-2"
          >
            Revenir à la connexion ?
          </span>
        )}
      </form>
    </div>
  );
}
